import type { EventPrivacy } from '~/features/events/lib/event-privacy'
import * as m from '~/paraglide/messages'
import type { EventPrice } from '~/shared/types/event'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { EventPriceSelector } from './event-price-selector'
import { TicketLinkField } from './ticket-link-field'

interface EventPricingSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
}

function validatePrice(value: EventPrice): string | undefined {
  if (!value?.kind) {
    return m.pleaseSelectAPriceType()
  }

  if (value.kind === 'fixed') {
    if (value.priceFixed === undefined || value.priceFixed <= 0) {
      return m.pleaseEnterAPriceGreaterThan0()
    }
  }

  if (value.kind === 'range') {
    if (value.priceRangeMin === undefined || value.priceRangeMin < 0) {
      return m.pleaseEnterAValidMinimumPrice()
    }
    if (value.priceRangeMax === undefined || value.priceRangeMax < 0) {
      return m.pleaseEnterAValidMaximumPrice()
    }
    if (value.priceRangeMax <= value.priceRangeMin) {
      return m.maximumPriceMustBeGreaterThanMinimumPrice()
    }
  }

  return undefined
}

export function EventPricingSection({ form }: EventPricingSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.pricingTickets()}</CardTitle>
        <CardDescription>{m.setThePriceAndCapacityForYourEvent()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PriceField form={form} />
        <TicketLinkField form={form} />
        <CapacityField form={form} />
      </CardContent>
    </Card>
  )
}

export function PriceField({ form }: EventPricingSectionProps) {
  return (
    <form.Field
      name="price"
      validators={{
        onChange: ({ value }: { value: EventPrice }) => validatePrice(value),
      }}
    >
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <div data-field="price">
          <EventPriceSelector
            value={field.state.value as EventPrice}
            onChange={(price) => field.handleChange(price)}
            onBlur={field.handleBlur}
            error={field.state.meta.isTouched ? field.state.meta.errors?.[0] : undefined}
          />
        </div>
      )}
    </form.Field>
  )
}

export function CapacityField({ form }: EventPricingSectionProps) {
  return (
    <form.Field name="capacity">
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <div className="space-y-2">
          <Label htmlFor={field.name}>{m.capacityOptional()}</Label>
          <Input
            id={field.name}
            name={field.name}
            type="number"
            value={field.state.value ?? ''}
            onBlur={field.handleBlur}
            onChange={(e) =>
              field.handleChange(e.target.value ? Number(e.target.value) : undefined)
            }
            placeholder={m.maximumAttendees()}
            aria-describedby={`${field.name}-request-hint`}
          />
          <form.Subscribe
            selector={(state: { values: { privacy?: EventPrivacy } }) =>
              state.values.privacy === 'request'
            }
          >
            {(onRequest: boolean) =>
              onRequest && (
                <p id={`${field.name}-request-hint`} className="text-sm text-muted-foreground">
                  {m.capacityRequestHint()}
                </p>
              )
            }
          </form.Subscribe>
        </div>
      )}
    </form.Field>
  )
}
